import React, { FC, useContext, useEffect, useState } from 'react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import { Context } from '../../context';
import { ChatChart, splitChart } from './ChatChart';

interface ChatStep {
  text: string;
  tools: string[];
}

interface ChatMessage {
  message: string;
  direction: 'incoming' | 'outgoing';
  sender: string;
  steps?: ChatStep[];
}

const stepsStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#666',
  margin: '0 0 6px 0',
  padding: '4px 8px',
  background: '#f4f4f4',
  borderRadius: 8,
  maxWidth: '100%',
};

const StepsView: FC<{ steps: ChatStep[]; live: boolean }> = ({
  steps,
  live,
}) => {
  if (!steps.length) return null;
  const list = steps.map((s, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <div key={i} style={{ margin: '4px 0' }}>
      {s.text.trim()}
      {s.tools.map((t, j) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={j} style={{ fontStyle: 'italic', opacity: 0.8 }}>
          • {t}
        </div>
      ))}
    </div>
  ));
  // While running, keep steps visible; once finished, collapse them.
  return live ? (
    <div style={stepsStyle}>{list}</div>
  ) : (
    <details style={stepsStyle}>
      <summary style={{ cursor: 'pointer' }}>
        Show thinking ({steps.length} steps)
      </summary>
      {list}
    </details>
  );
};

// A new agent session is started once the current one is older than this...
const SESSION_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes
// ...or once this many questions have been asked in it.
const SESSION_MAX_QUESTIONS = 5;

interface SessionMeta {
  id: string;
  startedAt: number;
  questions: number;
}

const generateSessionId = (): string => (crypto as any).randomUUID();

const fabStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 28,
  left: 28,
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #e60000 0%, #a80000 100%)',
  border: 'none',
  boxShadow: '0 4px 20px rgba(230,0,0,0.4)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  color: '#fff',
  fontSize: 24,
  transition: 'transform 0.15s ease',
};

const panelStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 96,
  left: 28,
  width: 360,
  height: 500,
  zIndex: 9999,
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
};

const decodeJwt = (token: string): any => {
  try {
    if (!token || token === 'NULL') return null;
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const initialMessages: ChatMessage[] = [
  {
    message: 'Hi! Ask me anything about m-mama data or statistics.',
    direction: 'incoming',
    sender: 'Assistant',
  },
];

export const ChatBubble: FC = () => {
  const { tokenStorage, getAccessToken } = useContext(Context);
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [liveSteps, setLiveSteps] = useState<ChatStep[]>([]);
  const [liveText, setLiveText] = useState('');

  const accessToken = getAccessToken(tokenStorage);
  const decoded = decodeJwt(accessToken);
  const userId = decoded?.user_id || decoded?.username || 'anonymous';

  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [, setSessionId] = useState<string>('');

  // Synchronize chat state when the active user login session changes
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`mmama_chat_messages_${userId}`);
      setMessages(stored ? JSON.parse(stored) : initialMessages);

      let id = localStorage.getItem(`mmama_chat_session_id_${userId}`);
      if (!id) {
        id = generateSessionId();
        localStorage.setItem(`mmama_chat_session_id_${userId}`, id);
      }
      setSessionId(id);
    } catch {
      setMessages(initialMessages);
      setSessionId(generateSessionId());
    }
  }, [userId]);

  // Sync messages to localStorage whenever they change for this user
  useEffect(() => {
    if (!userId || userId === 'anonymous') return;
    try {
      localStorage.setItem(
        `mmama_chat_messages_${userId}`,
        JSON.stringify(messages),
      );
    } catch (e) {
      console.error('Failed to save chat history', e);
    }
  }, [messages, userId]);

  // Returns the session id to use for this question, rotating to a fresh
  // session when the current one is too old or has had too many questions.
  // `renewal` is set only when an existing session was replaced (not on first use).
  const resolveSession = (): {
    id: string;
    renewal: 'time' | 'questions' | null;
  } => {
    const metaKey = `mmama_chat_session_meta_${userId}`;
    const idKey = `mmama_chat_session_id_${userId}`;
    const now = Date.now();

    let stored: SessionMeta | null = null;
    try {
      stored = JSON.parse(localStorage.getItem(metaKey) || 'null');
    } catch {
      stored = null;
    }

    let renewal: 'time' | 'questions' | null = null;
    if (stored && now - stored.startedAt >= SESSION_MAX_AGE_MS)
      renewal = 'time';
    else if (stored && stored.questions >= SESSION_MAX_QUESTIONS)
      renewal = 'questions';
    const expired = !stored || renewal !== null;

    let meta: SessionMeta = {
      id: generateSessionId(),
      startedAt: now,
      questions: 0,
    };
    if (!expired && stored) meta = stored;
    meta.questions += 1;

    try {
      localStorage.setItem(metaKey, JSON.stringify(meta));
      localStorage.setItem(idKey, meta.id);
    } catch {
      /* storage unavailable: session still works for this send */
    }
    setSessionId(meta.id);
    return { id: meta.id, renewal };
  };

  const handleSend = async (text: string) => {
    const { id: activeSessionId, renewal } = resolveSession();
    const userMsg: ChatMessage = {
      message: text,
      direction: 'outgoing',
      sender: 'User',
    };
    if (renewal) {
      // Session expired: clear the old conversation and tell the user why.
      let reason = `This conversation has been open for over ${
        SESSION_MAX_AGE_MS / 60000
      } minutes`;
      if (renewal === 'questions') {
        reason = `You've reached the limit of ${SESSION_MAX_QUESTIONS} questions in this conversation`;
      }
      const notice: ChatMessage = {
        message: `${reason}, so I've started a fresh one. Earlier messages have been cleared and I won't remember them.`,
        direction: 'incoming',
        sender: 'Assistant',
      };
      setMessages([notice, userMsg]);
    } else {
      setMessages((prev) => [...prev, userMsg]);
    }
    setIsTyping(true);

    const steps: ChatStep[] = [];
    let current = ''; // text of the message currently being written
    let currentTools: string[] = [];
    let finished = false;
    let errorMessage = '';

    const syncLive = () => {
      setLiveSteps([...steps]);
      setLiveText(current);
    };

    const handleEvent = (evt: any) => {
      if (evt.type === 'delta') {
        current += evt.text;
      } else if (evt.type === 'tool') {
        currentTools.push(evt.name);
      } else if (evt.type === 'step_end') {
        steps.push({ text: current, tools: currentTools });
        current = '';
        currentTools = [];
      } else if (evt.type === 'done') {
        finished = true;
      } else if (evt.type === 'error') {
        errorMessage = evt.message || 'Unknown error';
      }
      syncLive();
    };

    try {
      const activeToken: string = getAccessToken(tokenStorage);
      const response = await fetch('/api/bedrock/chat/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${activeToken}`,
        },
        body: JSON.stringify({ message: text, session_id: activeSessionId }),
      });
      if (!response.ok || !response.body)
        throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        lines.filter(Boolean).forEach((line) => {
          try {
            handleEvent(JSON.parse(line));
          } catch {
            /* ignore malformed line */
          }
        });
      }

      if (errorMessage || !finished)
        throw new Error(errorMessage || 'Stream ended early');

      const agentMsg: ChatMessage = {
        message: current || '(no response)',
        direction: 'incoming',
        sender: 'Assistant',
        steps,
      };
      setMessages((prev) => [...prev, agentMsg]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          message: 'Sorry, could not reach the assistant. Please try again.',
          direction: 'incoming',
          sender: 'Assistant',
          steps,
        },
      ]);
    } finally {
      setIsTyping(false);
      setLiveSteps([]);
      setLiveText('');
    }
  };

  return (
    <>
      {/* Expandable chat panel */}
      {isOpen && (
        <div style={panelStyle} id="chat-panel">
          <MainContainer>
            <ChatContainer>
              <MessageList
                style={{ paddingTop: '10px' }}
                typingIndicator={
                  isTyping && !liveText && liveSteps.length === 0 ? (
                    <TypingIndicator content="Assistant is thinking…" />
                  ) : null
                }
              >
                {messages.map((msg, i) => {
                  const { text: msgText, chart } = splitChart(msg.message);
                  return (
                    <Message
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      model={{
                        direction: msg.direction,
                        position: 'single',
                        sender: msg.sender,
                      }}
                    >
                      <Message.CustomContent>
                        {msg.steps && msg.steps.length > 0 && (
                          <StepsView steps={msg.steps} live={false} />
                        )}
                        <div style={{ whiteSpace: 'pre-wrap' }}>{msgText}</div>
                        {chart && <ChatChart spec={chart} />}
                      </Message.CustomContent>
                    </Message>
                  );
                })}
                {isTyping && (liveSteps.length > 0 || liveText) && (
                  <Message
                    model={{
                      direction: 'incoming',
                      position: 'single',
                      sender: 'Assistant',
                    }}
                  >
                    <Message.CustomContent>
                      <StepsView steps={liveSteps} live />
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {splitChart(liveText).text}
                      </div>
                    </Message.CustomContent>
                  </Message>
                )}
              </MessageList>
              <MessageInput
                id="chat-input"
                placeholder="Ask a question…"
                onSend={handleSend}
                attachButton={false}
                style={{ paddingBottom: '10px', paddingTop: '10px' }}
                onPaste={(e: React.ClipboardEvent) => {
                  e.preventDefault();
                  const text = e.clipboardData.getData('text/plain');
                  const selection = window.getSelection();
                  if (selection && selection.rangeCount) {
                    selection.deleteFromDocument();
                    selection
                      .getRangeAt(0)
                      .insertNode(document.createTextNode(text));
                    selection.collapseToEnd();
                    const activeEl = document.activeElement || e.target;
                    if (activeEl) {
                      activeEl.dispatchEvent(
                        new Event('input', { bubbles: true }),
                      );
                    }
                  }
                }}
              />
            </ChatContainer>
          </MainContainer>
        </div>
      )}

      {/* Floating action button */}
      <button
        id="chat-toggle-btn"
        type="button"
        style={fabStyle}
        onClick={() => setIsOpen((o) => !o)}
        aria-label={isOpen ? 'Close chat' : 'Open chat assistant'}
      >
        {isOpen ? '✕' : '💬'}
      </button>
    </>
  );
};
